"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import clsx from "clsx";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { SelectCell, ArrowIcon } from "./SelectCell";
import MoreButton from "@/components/Resources/MoreButton";

interface NavItem {
  name: string;
  href: string;
  hasDropdown?: boolean;
  pathname: string;
}

interface ProductItem {
  menuLabel: string;
  activeLabel?: string;
  detailTitle: string;
  shortName: string;
  tagline: string;
  description: string;
  image: string;
  icon: string;
  href: string;
  features: string[];
}

interface ServiceSection {
  category: string;
  items: string[];
}

interface ServiceCategory {
  name: string;
  icon: string;
  sections: ServiceSection[];
}

// A sub-page inside a "simple" section mega-menu (OT Security / Resources /
// Company). `image` is optional — items without artwork render text-only.
// `imageFit` is "contain" for line-art icons (radar/ICS/circuit) and "cover"
// for full-bleed brand photos (which are wide banners that would otherwise
// shrink to a thin strip in the preview box).
interface SectionMenuItem {
  label: string;
  description: string;
  href: string;
  image?: string;
  imageFit?: "contain" | "cover";
}

const navItems: NavItem[] = [
  { name: "HOME", href: "/", pathname: "/" },
  {
    // There is no /products landing page, so the nav item points at the first
    // product in the lineup (productItems below / the Products page tabs) —
    // that moved from Q-Suite to HackGrid with the reorder.
    name: "PRODUCTS",
    href: "/products/hackgrid",
    pathname: "/products",
    hasDropdown: true,
  },
  {
    name: "SERVICES",
    href: "/service",
    pathname: "/service",
    hasDropdown: true,
  },
  {
    name: "OT SECURITY",
    href: "/ot-security/ot-ics-simulation",
    pathname: "/ot-security",
    hasDropdown: true,
  },
  {
    name: "RESOURCES",
    href: "/resources",
    pathname: "/resources",
    hasDropdown: true,
  },
  {
    name: "COMPANY",
    href: "/company/about-us",
    pathname: "/company",
    hasDropdown: true,
  },
  { name: "CONTACT", href: "/contact/singapore", pathname: "/contact" },
];

// Order matches the Products page tabs (the canonical source): HackGrid →
// SHRED → Q-SUITE → DarkSight. Keep in sync with the `tabs` array in
// src/app/products/layout.tsx and the home page sections. (The Footer still
// lists Q-Suite first — it has not been switched over yet.)
//
// The first entry is also what the mega menu previews before you hover a row
// (see the `productItems[0]` fallback below) and where the PRODUCTS nav item
// links, so reordering this array moves both.
//
// Two label registers on purpose: `menuLabel` (the clickable cells in the left
// column, and the mobile menu) is the bare product name — HACKGRID / SHRED /
// Q-SUITE / DARKSIGHT — matching the Products page tab row, while the detail
// panel on the right keeps the full product title in `detailTitle` and its
// original `description`. Don't collapse the two: the short form is for
// navigation, the full title and copy are the panel's own content.
const productItems: ProductItem[] = [
  {
    menuLabel: "HACKGRID",
    detailTitle: "HackGrid Cyber Range",
    shortName: "hackgrid",
    tagline: "Cyber Range Platform",
    description:
      "Redefining cybersecurity training through realistic simulation and emulation. Sharpen practical skills, validate expertise, and tackle real-world challenges with confidence.",
    image: "/images/Header/Hackgrid.svg",
    icon: "/images/Header/Hackgrid.svg",
    href: "/products/hackgrid",
    features: [
      "Training Management",
      "Comprehensive Challenges",
      "Powerful Orchestration",
      "Flexible Scenarios",
    ],
  },
  {
    menuLabel: "SHRED",
    detailTitle: "SHRED",
    shortName: "shred",
    tagline: "Vulnerability Research Platform",
    description:
      "SHRED is a centralized vulnerability research platform that enables security teams to manage fuzzing campaigns, configure research engines, triage crash findings, and track vulnerabilities from discovery through remediation, all from a single interface.",
    image: "/images/Header/shred.svg",
    icon: "/images/Header/shred.svg",
    href: "/products/shred",
    features: [
      "KhaiCode Engine",
      "AI-Powered Vulnerability Research",
      "Fuzzing Management Platform",
    ],
  },
  {
    menuLabel: "Q-SUITE",
    activeLabel: "Q-SUITE",
    detailTitle: "Q-SUITE",
    shortName: "q-suite",
    tagline: "Offensive Security Platform",
    description:
      "Where Live Threats Drive Real Security Validation. ECQ delivers a unified offensive security platform built on nearly two decades of continuous vulnerability research and exploit development, bringing together one of the industry's most extensive and specialized exploit libraries.",
    image: "/images/Header/q-suite.png",
    icon: "/images/Header/q-suite.png",
    href: "/products/q-suite",
    features: ["Q-OT Pack", "Q-IoT Pack", "Q-Enterprise Pack", "Q-Zero Pack"],
  },
  {
    menuLabel: "DARKSIGHT",
    detailTitle: "DarkSight - Threat Intelligence",
    shortName: "darksight",
    tagline: "Dark Web Threat Intelligence",
    description:
      "A dark web threat intelligence platform that delivers real-time insight into leaked data, enabling organizations to monitor external exposure and manage response.",
    image: "/images/Header/Scarf_layer3.svg",
    icon: "/images/Header/Scarf_layer3.svg",
    href: "/products/darksight",
    features: [
      "Global Cyber Attack Trends Analysis",
      "Dark Web Data Leaks Monitoring",
      "Comprehensive Leaked Data Reports",
      "Real-time Threat Intelligence",
    ],
  },
];

const serviceCategories: ServiceCategory[] = [
  {
    name: "OFFEND",
    icon: "/images/Header/Offend.svg",
    sections: [
      {
        category: "ATTACK ALL AREAS",
        items: [
          "Scenario-based Penetration Test",
          "Adversary Simulation",
        ],
      },
      {
        category: "ASSESSMENT",
        items: [
          "Web Application Penetration Test",
          "Mobile Application Penetration Test",
          "Compromise Assessment",
        ],
      },
    ],
  },
  {
    name: "DEFEND",
    icon: "/images/Header/Defend.svg",
    sections: [
      {
        category: "ANALYSIS",
        items: [
          "Application Security Analysis",
          "Secure Code Review",
          "Offensive MDR",
        ],
      },
      {
        category: "REGULATORY COMPLIANCE",
        items: ["PCI Compliance", "ISO Standard", "Local Regulatory"],
      },
    ],
  },
];

// Products-style rich dropdowns for the nav sections that have a small, flat
// set of sub-pages (no sub-categories, unlike Products/Services). Keyed by the
// nav item `name` so the desktop nav can look one up directly.
const SECTION_MENUS: Record<string, { items: SectionMenuItem[] }> = {
  "OT SECURITY": {
    items: [
      {
        label: "OT/ICS Simulation",
        href: "/ot-security/ot-ics-simulation",
        image: "/images/OTSecurity/cyber-range/hero-radar.png",
        description:
          "OT Cyber Range simulates your actual industrial environment end-to-end — PLCs, configurations, security controls, and network topology — so your team can train and defend before a real threat demands it.",
      },
      {
        label: "AID ICS Framework",
        href: "/ot-security/aid-ics-framework",
        image: "/images/OTSecurity/aid-ics/ics-framework.svg",
        description:
          "OT/ICS security services to protect critical infrastructure. Simulate attacks, assess exposure, and harden industrial control systems.",
      },
    ],
  },
  RESOURCES: {
    items: [
      {
        label: "Zero-day Tracker",
        href: "/resources/zero-day-tracker",
        image: "/images/Resource/zero_day_tracker.svg",
        description:
          "Track ECQ’s zero-day and unpublished vulnerability research. Stay informed on emerging security issues and mitigation guidance.",
      },
      {
        label: "Security Advisories",
        href: "/resources/security-advisories",
        image: "/images/Resource/security_advisories.svg",
        description:
          "ECQ security advisories for affected products and vulnerabilities. Read impact details, technical analysis, and recommended mitigations.",
      },
      {
        label: "White Paper",
        href: "/resources/white-paper",
        image: "/images/Resource/white_paper.svg",
        description:
          "Read ECQ cybersecurity white papers written by our consultants and researchers, covering offensive security, application security, and OT/ICS topics.",
      },
    ],
  },
  COMPANY: {
    items: [
      {
        label: "About Us",
        href: "/company/about-us",
        image: "/images/Header/ECQ_logo.svg",
        imageFit: "contain",
        description:
          "ECQ is a network security company offering offensive security services and solutions focused on active offensive and defensive positioning.",
      },
      {
        label: "Team",
        href: "/company/team",
        image: "/images/Company/team.jpg",
        imageFit: "cover",
        description:
          "Behind every successful project is a dedicated team committed to excellence. At ECQ, our achievements reflect the expertise, innovation, and collaboration of our professionals, who consistently deliver high-quality solutions and create lasting value for our clients.",
      },
      {
        label: "Customers",
        href: "/company/customers",
        // Figma node 2662:52081 ("Customer 1"). The 596×446 artboard exports
        // with no ground of its own, so the panel's #1d1e20 shows through and
        // the drawing sits on the same dark as the design. "contain" over
        // "cover": the clasped hands bleed off the left and right edges of the
        // frame, and filling the 300×250 box would trim ~17px off each arm.
        image: "/images/Company/customers.svg",
        imageFit: "contain",
        description:
          "The trust of our customers is our greatest achievement. Their feedback reflects the quality of our services, our commitment to excellence, and the long-term partnerships we strive to build with every client.",
      },
      // Careers is hidden from navigation on request — also out of the Company
      // tab bar (src/app/company/layout.tsx) and the Footer. /company/careers
      // and its JD pages still exist; restoring the entry is the whole job. The
      // artwork it used needs a fixed-dimension asset when it comes back:
      // careers-chair.svg is width/height:100% with no intrinsic ratio and
      // distorts under object-contain in the wide preview box, so the entry
      // carried /images/Company/careers-chair-icon.svg (real 221×276) instead.
    ],
  },
};

// Menu labels whose route folder differs from the slugified label.
const SERVICE_ROUTE_OVERRIDES: Record<string, string> = {
  "Web Application Penetration Test": "web-application",
  "Mobile Application Penetration Test": "mobile-application",
};

const slugify = (value: string) => value.toLowerCase().replace(/\s+/g, "-");

const serviceItemSlug = (itemName: string) =>
  SERVICE_ROUTE_OVERRIDES[itemName] ?? slugify(itemName);

/** True when `pathname` is `href` itself or a page nested under it. */
const isUnder = (pathname: string, href: string) =>
  pathname === href || pathname.startsWith(`${href}/`);

/**
 * Index of the *most specific* matching href, or -1.
 *
 * Longest-match matters because detail pages live under a listing href (an
 * advisory page under "/resources/security-advisories", a job under
 * "/company/careers"), and because a menu may mix a parent and its child.
 */
const bestMatchIndex = (pathname: string, hrefs: string[]) => {
  let best = -1;
  hrefs.forEach((href, i) => {
    if (!isUnder(pathname, href)) return;
    if (best < 0 || href.length > hrefs[best].length) best = i;
  });
  return best;
};

const sectionMenuActiveIndex = (pathname: string, name: string) =>
  bestMatchIndex(
    pathname,
    (SECTION_MENUS[name]?.items ?? []).map((it) => it.href),
  );

function MenuChild({
  label,
  current,
  onClick,
}: {
  label: string;
  /** This item is the page you are on. */
  current?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={current ? "page" : undefined}
      className={clsx(
        "flex items-center gap-4 h-8 px-6 rounded-lg w-full group/item cursor-pointer text-left transition-colors duration-200",
        // Red is reserved for the current page. Hover brightens to white
        // instead — the same split SelectCell uses — so hovering a neighbour
        // cannot make two rows look like "you are here".
        current
          ? "text-primary"
          : "text-text-default hover:text-text-highlight",
      )}
    >
      <span className="flex-1 text-[16px] font-medium leading-6 tracking-[0.5px]">
        {label}
      </span>
      <ArrowIcon
        className={clsx(
          "flex-shrink-0 transition-opacity duration-200",
          current
            ? "text-primary opacity-100"
            : "text-text-highlight opacity-0 group-hover/item:opacity-100",
        )}
      />
    </button>
  );
}

// Chevron used by every expandable row in the mobile menu. Points down when
// collapsed, flips up when the section is open — the affordance the user asked
// for so a dropdown row is obviously tappable.
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={clsx(
        "w-4 h-4 flex-shrink-0 transition-transform duration-300",
        open && "rotate-180",
      )}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);

  // Which cell the pointer is previewing inside an open mega menu. null =
  // "nothing hovered yet", which lets the route decide (see productHighlight /
  // serviceHighlight below). Kept separate from the route so hovering can never
  // erase the "you are here" marker.
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [hoveredServiceTab, setHoveredServiceTab] = useState<string | null>(
    null,
  );
  const [hoveredSectionIndex, setHoveredSectionIndex] = useState<number | null>(
    null,
  );

  // Simple section mega-menus (OT Security / Resources / Company). Only one is
  // ever open, so a single "which one" string is enough. `lastSectionRef` keeps
  // the last-opened menu's key so its content stays rendered through the close
  // (height/opacity) transition.
  const [openSectionMenu, setOpenSectionMenu] = useState<string | null>(null);
  const sectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSectionRef = useRef<string | null>(null);

  // Mobile overlay accordion. One top-level row open at a time, keyed by nav
  // name, so exclusivity is structural instead of a set of booleans that have
  // to remember to clear each other.
  const [mobileOpenRow, setMobileOpenRow] = useState<string | null>(null);
  const [mobileActiveServiceCategory, setMobileActiveServiceCategory] =
    useState<string | null>(null);
  // Which service SECTION (e.g. "OFFEND|ATTACK ALL AREAS") is expanded — the
  // level below the category. Keyed "category|section" so only one opens at a
  // time.
  const [mobileOpenServiceSection, setMobileOpenServiceSection] = useState<
    string | null
  >(null);

  const isDesktop = useIsDesktop();
  const [scrolled, setScrolled] = useState(false);
  const productsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const servicesTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Same rule as every other level of the menu — including the "/" case, which
  // isUnder() already treats as exact-only.
  const isNavItemActive = (item: NavItem) => isUnder(pathname, item.pathname);

  /**
   * `aria-current` for a menu entry: "page" only when the entry links to the
   * URL you are on, "true" when it merely owns it (a branch row, or a listing
   * whose detail page you are reading). Announcing a branch as "current page"
   * would be a lie — CONTACT points at /contact/singapore while you may be on
   * /contact/vietnam.
   */
  const ariaCurrentFor = (href: string, owns = isUnder(pathname, href)) =>
    pathname === href ? "page" : owns ? true : undefined;

  // ---- Where the current route sits in the menu tree ----------------------
  // The route is the single source of truth for "active": every menu level
  // (nav item → product/category → section → leaf) reads from these, so what
  // is highlighted always matches the page you are on.

  /** shortName of the product page currently open, else null. */
  const routeProduct = useMemo(() => {
    const idx = bestMatchIndex(
      pathname,
      productItems.map((p) => p.href),
    );
    return idx < 0 ? null : productItems[idx].shortName;
  }, [pathname]);

  /**
   * The service branch the route points at. `section`/`item` are null when the
   * route stops short (e.g. /service/offend), and `item` alone is null on a
   * section landing page (/service/defend/regulatory-compliance).
   */
  const routeService = useMemo(() => {
    const [, root, categorySlug, slug] = pathname.split("/");
    if (root !== "service" || !categorySlug) return null;
    const category = serviceCategories.find(
      (c) => c.name.toLowerCase() === categorySlug,
    );
    if (!category) return null;
    for (const section of category.sections) {
      if (slug && slugify(section.category) === slug) {
        return { category: category.name, section: section.category, item: null };
      }
      const item = section.items.find((it) => serviceItemSlug(it) === slug);
      if (item) {
        return { category: category.name, section: section.category, item };
      }
    }
    return { category: category.name, section: null, item: null };
  }, [pathname]);

  /**
   * Nav name of the simple section menu (OT / Resources / Company) the route
   * belongs to. Falls back to the nav item's own pathname so a branch landing
   * page that is not itself a menu entry (/resources) still expands its branch
   * — it just has no leaf to mark.
   */
  const routeSectionMenu = useMemo(
    () =>
      Object.keys(SECTION_MENUS).find(
        (name) =>
          sectionMenuActiveIndex(pathname, name) >= 0 ||
          isUnder(
            pathname,
            navItems.find((n) => n.name === name)?.pathname ?? "\0",
          ),
      ) ?? null,
    [pathname],
  );

  // "SERVICE|Item" for the leaf page currently open. Drives the red accent on
  // the leaf itself; section headers derive their own state from
  // routeService.section via isSectionActive.
  const activeServiceItemKey = routeService?.item
    ? `${routeService.category}|${routeService.item}`
    : null;

  /** Whether this section header owns the current page. */
  const isSectionActive = (categoryName: string, sectionCategory: string) =>
    routeService?.category === categoryName &&
    routeService?.section === sectionCategory;

  const handleProductItemClick = (href: string) => {
    setProductsDropdownOpen(false);
    setMobileMenuOpen(false);
    router.push(href);
  };

  const handleServiceItemClick = (serviceName: string, itemName: string) => {
    setServicesDropdownOpen(false);
    setMobileMenuOpen(false);
    router.push(
      `/service/${serviceName.toLowerCase()}/${serviceItemSlug(itemName)}`,
    );
  };

  // Section mega-menu (OT / Resources / Company) hover + navigation.
  const handleSectionMouseEnter = (name: string) => {
    if (!isDesktop) return;
    if (sectionTimeoutRef.current) clearTimeout(sectionTimeoutRef.current);
    lastSectionRef.current = name;
    setOpenSectionMenu(name);
    // Drop the previous pointer preview so the panel opens on the sub-page you
    // are actually on. Seeding here (rather than on close) keeps the panel
    // showing what you hovered until it is really gone — resetting on close
    // swaps the content back while the panel is still fully visible.
    setHoveredSectionIndex(null);
    setProductsDropdownOpen(false);
    setServicesDropdownOpen(false);
  };

  const handleSectionMouseLeave = () => {
    if (!isDesktop) return;
    sectionTimeoutRef.current = setTimeout(() => {
      setOpenSectionMenu(null);
    }, 200);
  };

  const handleSectionItemClick = (href: string) => {
    setOpenSectionMenu(null);
    setMobileMenuOpen(false);
    router.push(href);
  };

  /** Mobile overlay: expand this row and collapse whichever was open. */
  const toggleMobileRow = (name: string) =>
    setMobileOpenRow((prev) => (prev === name ? null : name));

  const handleProductsMouseEnter = () => {
    if (!isDesktop) return;
    if (productsTimeoutRef.current) clearTimeout(productsTimeoutRef.current);
    if (!productsDropdownOpen) {
      // Opening: forget the last preview so the route decides what is shown.
      setHoveredProduct(null);
    }
    setProductsDropdownOpen(true);
    setServicesDropdownOpen(false);
    setOpenSectionMenu(null);
  };

  const handleProductsMouseLeave = () => {
    if (!isDesktop) return;
    productsTimeoutRef.current = setTimeout(() => {
      setProductsDropdownOpen(false);
    }, 200);
  };

  const handleServicesMouseEnter = () => {
    if (!isDesktop) return;
    if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
    if (!servicesDropdownOpen) {
      setHoveredServiceTab(null);
    }
    setServicesDropdownOpen(true);
    setProductsDropdownOpen(false);
    setOpenSectionMenu(null);
  };

  const handleServicesMouseLeave = () => {
    if (!isDesktop) return;
    servicesTimeoutRef.current = setTimeout(() => {
      setServicesDropdownOpen(false);
    }, 200);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isDesktop) {
        const target = event.target as HTMLElement;
        if (!target.closest(".dropdown-container")) {
          setProductsDropdownOpen(false);
          setServicesDropdownOpen(false);
          setOpenSectionMenu(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [isDesktop]);

  useEffect(() => {
    return () => {
      if (productsTimeoutRef.current) clearTimeout(productsTimeoutRef.current);
      if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
      if (sectionTimeoutRef.current) clearTimeout(sectionTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setProductsDropdownOpen(false);
    setServicesDropdownOpen(false);
    setOpenSectionMenu(null);
  }, [pathname]);

  // A pointer preview wins while a panel is open; with nothing hovered the
  // route decides. These may be null — "no cell is the current page" is exactly
  // what routes absent from the menu (/products/scarf, /resources,
  // /service/manage/*) must render instead of falsely painting the first item.
  const productHighlight = hoveredProduct ?? routeProduct;
  const serviceHighlight =
    hoveredServiceTab ?? routeService?.category.toLowerCase() ?? null;

  // The right-hand preview always needs something to show, so it falls back to
  // the first entry — a fallback that must never mark a cell as current.
  const activeProduct =
    productItems.find((p) => p.shortName === productHighlight) ??
    productItems[0];
  const activeService =
    serviceCategories.find((c) => c.name.toLowerCase() === serviceHighlight) ??
    serviceCategories[0];

  // Opening the mobile menu reveals where you already are: expand the branch
  // that owns the current route (and only that branch).
  useEffect(() => {
    if (!mobileMenuOpen) return;
    setMobileOpenRow(
      routeProduct !== null
        ? "PRODUCTS"
        : routeService !== null
          ? "SERVICES"
          : routeSectionMenu,
    );
    setMobileActiveServiceCategory(routeService?.category ?? null);
    setMobileOpenServiceSection(
      routeService?.section
        ? `${routeService.category}|${routeService.section}`
        : null,
    );
  }, [mobileMenuOpen, routeProduct, routeService, routeSectionMenu]);

  return (
    <>
      {/* Backdrop blur + dim behind the desktop dropdown (Figma "Overlay":
          backdrop-blur 25px, #1d1e20 @ 80%). Sits below the header (z-50) so
          the nav bar + mega menu stay sharp while the page content blurs. */}
      <div
        aria-hidden
        onMouseEnter={() => {
          setProductsDropdownOpen(false);
          setServicesDropdownOpen(false);
          setOpenSectionMenu(null);
        }}
        className={clsx(
          "hidden lg:block fixed inset-0 z-40 bg-[#1d1e20]/80 backdrop-blur-[25px] transition-opacity duration-300",
          productsDropdownOpen || servicesDropdownOpen || openSectionMenu
            ? "opacity-100"
            : "opacity-0 pointer-events-none",
        )}
      />
      <header
        className={clsx(
          // Same gutters + 1300px column as the page body and footer
          // (px-4 sm:px-6 lg:px-8 around max-w-[1300px]). The old fixed
          // lg:px-[70px] only lined up at exactly 1440 — the Figma frame
          // width, where vw-64 first exceeds 1300; past that the logo/nav
          // stayed pinned 70px from the edge while the content column kept
          // centering (240px off at 1920).
          "fixed top-0 z-50 w-full h-[72px] flex items-center px-4 sm:px-6 lg:px-8 transition-all duration-500 dropdown-container",
          // When a mega menu is open, match its Surface 4 (#1d1e20) so the header
          // + dropdown read as one continuous panel (Figma node 83:78107).
          // Plain scrolled state keeps Surface 1 (#202328).
          productsDropdownOpen || servicesDropdownOpen || openSectionMenu
            ? "bg-[#1d1e20]"
            : scrolled
              ? "bg-surface-header"
              : "bg-transparent",
        )}
      >
        <div className="w-full max-w-[1300px] mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center z-50">
            <Image
              src="/images/Header/ECQ_logo.svg"
              alt="ECQ Logo"
              width={112}
              height={40}
              className="h-8 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => {
              const isActive = isNavItemActive(item);
              const isProducts = item.name === "PRODUCTS";
              const isServices = item.name === "SERVICES";
              const hasSectionMenu = Boolean(SECTION_MENUS[item.name]);
              // Whether THIS item's mega menu is the one on screen. It has to be
              // state rather than CSS :hover, because the moment you move down
              // into the panel the label stops being hovered — and an open menu
              // whose trigger shows nothing leaves you guessing which nav item
              // it belongs to.
              const isOpen = isProducts
                ? productsDropdownOpen
                : isServices
                  ? servicesDropdownOpen
                  : openSectionMenu === item.name;
              return (
                <div
                  key={item.name}
                  onMouseEnter={
                    isProducts
                      ? handleProductsMouseEnter
                      : isServices
                        ? handleServicesMouseEnter
                        : hasSectionMenu
                          ? () => handleSectionMouseEnter(item.name)
                          : undefined
                  }
                  onMouseLeave={
                    isProducts
                      ? handleProductsMouseLeave
                      : isServices
                        ? handleServicesMouseLeave
                        : hasSectionMenu
                          ? handleSectionMouseLeave
                          : undefined
                  }
                >
                  <Link
                    href={item.href}
                    aria-current={isActive ? ariaCurrentFor(item.href, true) : undefined}
                    className={clsx(
                      // Figma "Nav - Button": all states are Avenir Next Demi
                      // Bold 16/24 (body-1-heavy). Only the 3px bottom border
                      // changes between states — never the weight/size (see
                      // menu-active-no-size-change).
                      //
                      // There is deliberately NO "you are here" state here: the
                      // nav used to paint the current section red, and that was
                      // dropped on request. The underline is now purely a
                      // pointer affordance — and since red is no longer spoken
                      // for by the current page, the underline is red. `isOpen`
                      // is not a second selected state: it is that same hover
                      // held while the pointer is down inside the panel, which
                      // stops being :hover the moment you move off the label.
                      // (aria-current stays: it costs nothing visually and is
                      // how a screen reader still knows which section is open.)
                      //
                      // Exactly the header's own 72px, not py-6 (which made the
                      // box 75px and spilled 1.5px past the bar top and bottom).
                      // The mega menu panel is absolutely placed at top-[72px]
                      // and paints after the nav, so those 1.5px of overhang put
                      // 2 of the underline's 3px UNDER the panel the moment a
                      // menu opened — the mark thinned to a hairline exactly when
                      // you needed to read it. Text position is unchanged: its
                      // centre lands at 34.5px either way.
                      "body-1-heavy uppercase inline-flex items-center justify-center px-2 h-[72px] cursor-pointer transition-colors duration-200 border-b-[3px] text-text-highlight",
                      isOpen
                        ? "border-primary"
                        : "border-transparent hover:border-primary",
                    )}
                  >
                    {item.name}
                  </Link>
                </div>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden z-50 flex flex-col gap-1.5 w-8 h-8 justify-center items-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={clsx(
                "block w-6 h-0.5 bg-white transition-all duration-300",
                mobileMenuOpen && "rotate-45 translate-y-2",
              )}
            />
            <span
              className={clsx(
                "block w-6 h-0.5 bg-white transition-all duration-300",
                mobileMenuOpen && "opacity-0",
              )}
            />
            <span
              className={clsx(
                "block w-6 h-0.5 bg-white transition-all duration-300",
                mobileMenuOpen && "-rotate-45 -translate-y-2",
              )}
            />
          </button>
        </div>

        {/* PRODUCTS MEGA MENU. `inert` when closed: the panel is only clipped
            to h-0, so without it every control inside stays focusable (and in
            the a11y tree) — keyboard users landed on invisible cells right
            after the nav, including one carrying aria-current. */}
        <div
          onMouseEnter={handleProductsMouseEnter}
          onMouseLeave={handleProductsMouseLeave}
          inert={!productsDropdownOpen}
          className={clsx(
            "absolute top-[72px] left-0 right-0 bg-[#1d1e20] border-t border-[rgba(60,60,67,0.6)] rounded-b-2xl overflow-hidden transition-all duration-300 ease-out",
            productsDropdownOpen
              ? "h-auto xl:h-[400px] opacity-100"
              : "h-0 opacity-0 pointer-events-none",
          )}
        >
          <div className="w-full h-full px-4 sm:px-6 lg:px-8 py-6">
            <div className="mx-auto w-full max-w-[1300px] h-full">
              {/* One desktop layout from `lg` — the same breakpoint that shows
                  the nav which opens this panel (and that `useIsDesktop` gates
                  the hover handlers on). It used to start at `xl` with an
                  accordion of icon cards below it, which made 1024–1279px a
                  third look that matched neither the desktop panel nor the
                  mobile overlay. Only the preview artwork drops below `xl`
                  (see below): at 1024 the 420px cell column leaves 499px, and
                  a 369px image would squeeze the copy to ~66px. */}
              <div className="hidden lg:flex gap-5">
                {/* Left Section - Product Tabs */}
                <div className="w-[420px] flex-shrink-0 flex flex-col gap-2">
                  {productItems.map((product) => (
                    <SelectCell
                      key={product.shortName}
                      label={product.menuLabel}
                      activeLabel={product.activeLabel}
                      active={hoveredProduct === product.shortName}
                      current={routeProduct === product.shortName}
                      onMouseEnter={() => setHoveredProduct(product.shortName)}
                      onClick={() => handleProductItemClick(product.href)}
                    />
                  ))}
                </div>

                {/* Divider */}
                <div className="w-px bg-[#3F444A]/50 self-stretch"></div>

                {/* Right Section - Image + Details. Stretches (no `items-start`)
                    so the artwork below can centre itself. The row above has no
                    `h-full`, so the height it stretches to is the 4-cell left
                    column (280px) — which is what the artwork should line up
                    against here.

                    `xl:pl-5` pairs with the row's `gap-10` to centre the artwork
                    HORIZONTALLY — see the section panel below for the full
                    reasoning; the arithmetic is identical. */}
                <div className="flex-1 flex gap-4 xl:pl-5">
                  {activeProduct && (
                    <div
                      key={activeProduct.shortName}
                      className="w-full flex gap-10 items-start svc-panel-enter"
                    >
                      {/* Product Image — `xl`+ only. Below that the copy takes
                          the whole right column instead of sharing it.
                          `self-center` for the same reason as the section panel:
                          a 250px box top-aligned against a 280px column left the
                          artwork riding high with dead space beneath it. */}
                      <div className="hidden xl:flex w-[369px] flex-shrink-0 h-[250px] relative items-center justify-center self-center">
                        <Image
                          src={activeProduct.image}
                          alt={activeProduct.detailTitle}
                          width={369}
                          height={250}
                          unoptimized
                          className="relative z-10 w-full h-full object-contain"
                        />
                      </div>

                      {/* Product Details. min-h (not fixed h) so long descriptions
                          (Q-SUITE / SHRED) can grow into the panel's spare height
                          instead of crushing the button; gap-6 guarantees a gap
                          between the copy and the "View details" button, while
                          justify-between still pins the button to the bottom for
                          short descriptions (aligned with the 250px image). */}
                      <div className="flex-1 flex flex-col min-h-[250px] justify-between items-start gap-6">
                        <div className="flex flex-col gap-2 w-full">
                          <h3 className="headline-4 text-text-highlight whitespace-nowrap">
                            {activeProduct.detailTitle}
                          </h3>
                          {/* Cap at 8 lines so the longest copy (Q-SUITE) can't
                              overflow the panel's fixed xl height and push the
                              button out of view at ~1280px, where the artwork
                              leaves the copy its narrowest column. Below xl the
                              copy is wider (no artwork) and the panel is
                              h-auto, so nothing is clamped there. */}
                          <p className="body-1 text-text-default line-clamp-8">
                            {activeProduct.description}
                          </p>
                        </div>
                        <MoreButton
                          href={activeProduct.href}
                          label="View details"
                          onClick={() => {
                            setProductsDropdownOpen(false);
                            setMobileMenuOpen(false);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SERVICES MEGA MENU (see PRODUCTS above re: `inert`). */}
        <div
          onMouseEnter={handleServicesMouseEnter}
          onMouseLeave={handleServicesMouseLeave}
          inert={!servicesDropdownOpen}
          className={clsx(
            "absolute top-[72px] left-0 right-0 bg-[#1d1e20] border-t border-[rgba(60,60,67,0.6)] rounded-b-2xl overflow-hidden transition-all duration-300 ease-out",
            servicesDropdownOpen
              ? "h-auto opacity-100"
              : "h-0 opacity-0 pointer-events-none",
          )}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <div className="mx-auto w-full max-w-[1300px]">
              {/* One desktop layout from `lg` (see the Products panel above for
                  why the old xl-only split had to go). */}
              <div className="hidden lg:flex gap-5">
                {/* Left Section - Service Tabs */}
                <div className="w-[420px] flex-shrink-0 flex flex-col gap-2">
                  {serviceCategories.map((category) => (
                    <SelectCell
                      key={category.name}
                      label={category.name}
                      active={hoveredServiceTab === category.name.toLowerCase()}
                      current={routeService?.category === category.name}
                      onMouseEnter={() =>
                        setHoveredServiceTab(category.name.toLowerCase())
                      }
                    />
                  ))}
                </div>

                {/* Divider */}
                <div className="w-px bg-[#3F444A]/50 self-stretch"></div>

                {/* Right Section - Service Content */}
                <div className="flex-1">
                  {/* The two section groups sit side by side only from `xl`.
                      At 1024 the right column is 499px, so two groups would
                      leave ~200px of text width per item — enough to wrap
                      "Web Application Penetration Test" inside MenuChild's
                      fixed h-8 row. Stacked, each item gets the full 499px. */}
                  {activeService && (
                    <div
                      key={activeService.name}
                      className="flex flex-col gap-6 xl:flex-row xl:gap-4 svc-panel-enter"
                    >
                      {activeService.sections.map((section, sectionIndex) => (
                        <div
                          key={section.category}
                          className="xl:flex-1 svc-col-enter flex flex-col gap-4"
                          style={{
                            animationDelay: `${sectionIndex * 80}ms`,
                          }}
                        >
                          {/* Section header */}
                          <div className="border-b border-[rgba(151,151,151,0.5)] pb-2 h-8 flex items-center">
                            <h3
                              className={clsx(
                                "body-1-heavy uppercase",
                                isSectionActive(
                                  activeService.name,
                                  section.category,
                                )
                                  ? "text-primary"
                                  : "text-text-default",
                              )}
                            >
                              {section.category}
                            </h3>
                          </div>
                          {/* Items */}
                          <div className="flex flex-col gap-4">
                            {section.items.map((item, itemIndex) => {
                              const isCurrent =
                                activeServiceItemKey ===
                                `${activeService.name}|${item}`;
                              return (
                                <div
                                  key={item}
                                  className="svc-item-enter"
                                  style={{
                                    animationDelay: `${
                                      sectionIndex * 80 + itemIndex * 55
                                    }ms`,
                                  }}
                                >
                                  <MenuChild
                                    label={item}
                                    current={isCurrent}
                                    onClick={() =>
                                      handleServiceItemClick(
                                        activeService.name,
                                        item,
                                      )
                                    }
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION MEGA MENUS (OT Security / Resources / Company) — a lighter,
            Products-style panel (left item list + right preview) for sections
            with a small, flat set of sub-pages. Only one is open at a time; the
            last-open key is kept so content stays through the close transition.

            Renders from `lg`, like the Products/Services panels: the nav that
            opens it is `lg:flex` and the dim/blur backdrop is `lg:block`, so an
            xl-only panel meant 1024–1279px hovered these three items into a
            blurred, click-blocked page with no menu at all (and left their
            sub-pages unreachable from the header). The list + preview split now
            holds from `lg` too — only the artwork waits for `xl`, exactly as in
            the Products panel, so all three panel types show one shape. */}
        {(() => {
          const key = openSectionMenu ?? lastSectionRef.current;
          const items = key ? (SECTION_MENUS[key]?.items ?? []) : [];
          const routeIndex = key ? sectionMenuActiveIndex(pathname, key) : -1;
          // Preview: what the pointer is on, else the current page, else the
          // first item so the panel is never blank.
          const active = items[hoveredSectionIndex ?? routeIndex] ?? items[0];
          return (
            <div
              onMouseEnter={() => {
                if (!isDesktop || !key) return;
                if (sectionTimeoutRef.current)
                  clearTimeout(sectionTimeoutRef.current);
                setOpenSectionMenu(key);
              }}
              onMouseLeave={handleSectionMouseLeave}
              inert={!openSectionMenu}
              className={clsx(
                "hidden lg:block absolute top-[72px] left-0 right-0 bg-[#1d1e20] border-t border-[rgba(60,60,67,0.6)] rounded-b-2xl overflow-hidden transition-all duration-300 ease-out",
                openSectionMenu
                  ? "h-auto xl:h-[360px] opacity-100"
                  : "h-0 opacity-0 pointer-events-none",
              )}
            >
              <div className="w-full h-full px-4 sm:px-6 lg:px-8 py-6">
                <div className="mx-auto w-full max-w-[1300px] h-full flex gap-5">
                  {/* Left — sub-page list */}
                  <div className="w-[420px] flex-shrink-0 flex flex-col gap-2">
                    {items.map((it, idx) => (
                      <SelectCell
                        key={it.href}
                        label={it.label}
                        active={hoveredSectionIndex === idx}
                        current={routeIndex === idx}
                        onMouseEnter={() => setHoveredSectionIndex(idx)}
                        onClick={() => handleSectionItemClick(it.href)}
                      />
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="w-px bg-[#3F444A]/50 self-stretch" />

                  {/* Right — active item preview. Stretches (no `items-start`)
                      so the artwork below can centre itself against the panel's
                      full height.

                      `xl:pl-5` + the row's `gap-10` centre the artwork
                      HORIZONTALLY. The artwork box is flanked by the divider on
                      one side and the copy on the other, and those two gaps have
                      to match or the artwork sits off-centre no matter how the
                      image behaves inside its own box. It used to be 20px
                      (the column `gap-5`) on the left against `gap-16` (64px) on
                      the right — a 44px bias that pulled every preview toward
                      the divider. Now it is 20+20 = 40px on the left and 40px on
                      the right. Keep the two in step: `pl-<n>` + column gap must
                      equal the row gap. */}
                  <div className="flex flex-1 gap-4 xl:pl-5">
                    {active && (
                      <div
                        key={active.href}
                        className="w-full flex gap-10 items-start svc-panel-enter"
                      >
                        {active.image && (
                          <div
                            className={clsx(
                              // `xl`+ only, as in the Products panel: at 1024 the
                              // 420px cell column leaves 499px for the preview,
                              // which the copy needs in full.
                              //
                              // `self-center`: the panel is a fixed 360px at
                              // `xl` while this box is 250px, so top-aligning it
                              // left the artwork stuck under the header with a
                              // band of dead space beneath. Centring balances it
                              // vertically; the copy beside it stays top-aligned
                              // (its own `items-start`) so the heading still
                              // starts at the top of the panel.
                              "hidden xl:flex w-[300px] flex-shrink-0 h-[250px] relative items-center justify-center self-center",
                              active.imageFit === "cover" &&
                                "overflow-hidden rounded-[8px]",
                            )}
                          >
                            <Image
                              src={active.image}
                              alt={active.label}
                              width={300}
                              height={250}
                              unoptimized
                              className={clsx(
                                "relative z-10 w-full h-full",
                                active.imageFit === "cover"
                                  ? "object-cover"
                                  : "object-contain",
                              )}
                            />
                          </div>
                        )}
                        <div
                          className={clsx(
                            "flex-1 flex flex-col items-start",
                            // With artwork the text column matches the 250px image
                            // height (button pinned to the bottom). Text-only items
                            // instead hug their content so the button sits right
                            // under the copy rather than floating in empty space.
                            // Below xl the artwork is hidden, so every item takes
                            // the hug-content treatment there.
                            active.image
                              ? "gap-6 xl:gap-0 xl:h-[250px] xl:justify-between"
                              : "gap-6 max-w-[760px]",
                          )}
                        >
                          <div className="flex flex-col gap-2 w-full">
                            <h3 className="headline-4 text-text-highlight">
                              {active.label}
                            </h3>
                            {active.description && (
                              <p className="body-1 text-text-default">
                                {active.description}
                              </p>
                            )}
                          </div>
                          <MoreButton
                            href={active.href}
                            label="View details"
                            onClick={() => {
                              setOpenSectionMenu(null);
                              setMobileMenuOpen(false);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </header>

      {/* Dropdown animations */}
      <style>{`
        .svc-panel-enter { animation: svcPanelIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes svcPanelIn {
          from { opacity: 0; transform: translateX(10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .svc-col-enter { animation: svcColIn 0.3s ease-out both; }
        @keyframes svcColIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .svc-item-enter { animation: svcItemIn 0.28s ease-out both; }
        @keyframes svcItemIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Mobile Menu Overlay — text-only. Every row with a dropdown shows a
          chevron that toggles its sub-items; no icons/images on mobile (per
          request). Rows without a dropdown (HOME / CONTACT) are plain links. */}
      <div
        className={clsx(
          "lg:hidden fixed inset-0 z-40 bg-surface-header transition-transform duration-300",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        <nav className="flex flex-col w-full h-full px-4 sm:px-6 py-20 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = isNavItemActive(item);
            const isProducts = item.name === "PRODUCTS";
            const isServices = item.name === "SERVICES";
            const sectionMenu = SECTION_MENUS[item.name];
            const rowOpen = mobileOpenRow === item.name;

            // PRODUCTS — expandable, text-only list of products.
            if (isProducts) {
              return (
                <div
                  key={item.name}
                  className="w-full border-b border-surface-3 last:border-0"
                >
                  <button
                    onClick={() => toggleMobileRow(item.name)}
                    aria-expanded={rowOpen}
                    aria-current={isActive ? ariaCurrentFor(item.href, true) : undefined}
                    className={clsx(
                      // Red means "this branch owns the page you are on" and
                      // nothing else — the chevron already says "expanded".
                      // Colouring expanded rows too let two rows look equally
                      // like the current page.
                      "w-full flex items-center justify-between py-5 text-xl font-semibold uppercase transition-colors",
                      isActive ? "text-primary" : "text-white",
                    )}
                  >
                    {item.name}
                    <Chevron open={rowOpen} />
                  </button>

                  <div
                    className={clsx(
                      "overflow-hidden transition-all duration-300 ease-in-out",
                      rowOpen
                        ? "max-h-[1000px] opacity-100 mb-4"
                        : "max-h-0 opacity-0",
                    )}
                  >
                    <div className="flex flex-col border-l border-surface-3 ml-2 pl-4">
                      {productItems.map((product) => {
                        const isCurrent = routeProduct === product.shortName;
                        return (
                          <Link
                            key={product.shortName}
                            href={product.href}
                            onClick={() => setMobileMenuOpen(false)}
                            aria-current={ariaCurrentFor(product.href, isCurrent)}
                            className={clsx(
                              // font-medium on the active leaf is optical
                              // compensation, not emphasis: primary red only
                              // carries ~1/3 of #d6d6d6's contrast on this
                              // background (3.8:1 vs 11.5:1), so at Regular it
                              // reads thinner/smaller than its neighbours. 500
                              // is a real Medium face here; font-semibold would
                              // match up to Demi/700 and overshoot. Hover goes
                              // white so it can't be mistaken for current.
                              "block py-3 text-base transition-all duration-300 hover:translate-x-1",
                              isCurrent
                                ? "text-primary font-medium"
                                : "text-text-default hover:text-text-highlight",
                            )}
                          >
                            {product.menuLabel}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            // SERVICES — expandable, with nested category sub-dropdowns. Each
            // category is itself a dropdown, so it carries its own chevron.
            if (isServices) {
              return (
                <div
                  key={item.name}
                  className="w-full border-b border-surface-3 last:border-0"
                >
                  <button
                    onClick={() => toggleMobileRow(item.name)}
                    aria-expanded={rowOpen}
                    aria-current={isActive ? ariaCurrentFor(item.href, true) : undefined}
                    className={clsx(
                      "w-full flex items-center justify-between py-5 text-xl font-semibold uppercase transition-colors",
                      isActive ? "text-primary" : "text-white",
                    )}
                  >
                    {item.name}
                    <Chevron open={rowOpen} />
                  </button>

                  <div
                    className={clsx(
                      "overflow-hidden transition-all duration-300 ease-in-out",
                      rowOpen
                        ? "max-h-[2000px] opacity-100 mb-4"
                        : "max-h-0 opacity-0",
                    )}
                  >
                    <div className="flex flex-col border-l border-surface-3 ml-2 pl-4">
                      {serviceCategories.map((category) => {
                        const catOpen =
                          mobileActiveServiceCategory === category.name;
                        const catActive =
                          routeService?.category === category.name;
                        return (
                          <div key={category.name}>
                            <button
                              onClick={() =>
                                setMobileActiveServiceCategory(
                                  catOpen ? null : category.name,
                                )
                              }
                              aria-expanded={catOpen}
                              className={clsx(
                                "w-full flex items-center justify-between py-3 text-lg font-semibold uppercase transition-colors",
                                catActive ? "text-primary" : "text-white",
                              )}
                            >
                              {category.name}
                              <Chevron open={catOpen} />
                            </button>

                            <div
                              className={clsx(
                                "overflow-hidden transition-all duration-300",
                                catOpen
                                  ? "max-h-[1000px] opacity-100 pb-2"
                                  : "max-h-0 opacity-0",
                              )}
                            >
                              {category.sections.map((section, idx) => {
                                const sectionKey = `${category.name}|${section.category}`;
                                const sectionOpen =
                                  mobileOpenServiceSection === sectionKey;
                                return (
                                  <div
                                    key={idx}
                                    className="mb-2 last:mb-0 border-l border-surface-3 ml-2 pl-4"
                                  >
                                    <button
                                      onClick={() =>
                                        setMobileOpenServiceSection(
                                          sectionOpen ? null : sectionKey,
                                        )
                                      }
                                      className={clsx(
                                        // Section labels sit ABOVE the leaf links,
                                        // so they must not be smaller than them:
                                        // 16px uppercase semibold, same as the
                                        // desktop mega menu's section headers
                                        // (body-1-heavy). The mobile scale reads
                                        // 20 → 18 → 16 top to bottom.
                                        "w-full flex items-center justify-between py-2 text-base font-semibold uppercase tracking-[0.5px] transition-colors",
                                        isSectionActive(
                                          category.name,
                                          section.category,
                                        )
                                          ? "text-primary"
                                          : // text-text-default, not white/60: at
                                            // 16px the dimmer value read as
                                            // disabled, and this is the same
                                            // token the xl panel uses for an
                                            // inactive section header.
                                            "text-text-default",
                                      )}
                                      aria-expanded={sectionOpen}
                                    >
                                      {section.category}
                                      <Chevron open={sectionOpen} />
                                    </button>

                                    <div
                                      className={clsx(
                                        // pl-3: the leaves used to share the
                                        // section label's exact indent, so the
                                        // label didn't read as their parent.
                                        "overflow-hidden pl-3 transition-all duration-300",
                                        sectionOpen
                                          ? "max-h-[600px] opacity-100 pb-1"
                                          : "max-h-0 opacity-0",
                                      )}
                                    >
                                      {section.items.map((subItem) => {
                                        const isCurrent =
                                          activeServiceItemKey ===
                                          `${category.name}|${subItem}`;
                                        return (
                                          <Link
                                            key={subItem}
                                            href={`/service/${category.name.toLowerCase()}/${serviceItemSlug(subItem)}`}
                                            onClick={() =>
                                              setMobileMenuOpen(false)
                                            }
                                            aria-current={
                                              isCurrent ? "page" : undefined
                                            }
                                            className={clsx(
                                              "block py-2 text-base transition-all duration-300 hover:translate-x-1",
                                              isCurrent
                                                ? "text-primary font-medium"
                                                : "text-text-default hover:text-text-highlight",
                                            )}
                                          >
                                            {subItem}
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            // OT SECURITY / RESOURCES / COMPANY — expandable, text-only sub-pages.
            if (sectionMenu) {
              const activeSubIndex = sectionMenuActiveIndex(
                pathname,
                item.name,
              );
              return (
                <div
                  key={item.name}
                  className="w-full border-b border-surface-3 last:border-0"
                >
                  <button
                    onClick={() => toggleMobileRow(item.name)}
                    aria-expanded={rowOpen}
                    aria-current={isActive ? ariaCurrentFor(item.href, true) : undefined}
                    className={clsx(
                      "w-full flex items-center justify-between py-5 text-xl font-semibold uppercase transition-colors",
                      isActive ? "text-primary" : "text-white",
                    )}
                  >
                    {item.name}
                    <Chevron open={rowOpen} />
                  </button>

                  <div
                    className={clsx(
                      "overflow-hidden transition-all duration-300 ease-in-out",
                      rowOpen
                        ? "max-h-[600px] opacity-100 mb-4"
                        : "max-h-0 opacity-0",
                    )}
                  >
                    <div className="flex flex-col border-l border-surface-3 ml-2 pl-4">
                      {sectionMenu.items.map((sub, subIndex) => {
                        const isCurrent = subIndex === activeSubIndex;
                        return (
                          <Link
                            key={sub.href}
                            href={sub.href}
                            onClick={() => setMobileMenuOpen(false)}
                            aria-current={ariaCurrentFor(sub.href, isCurrent)}
                            className={clsx(
                              "block py-3 text-base transition-all duration-300 hover:translate-x-1",
                              isCurrent
                                ? "text-primary font-medium"
                                : "text-text-default hover:text-text-highlight",
                            )}
                          >
                            {sub.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            // HOME / CONTACT — plain links (no dropdown, so no chevron).
            return (
              <div
                key={item.name}
                className="w-full border-b border-surface-3 last:border-0"
              >
                <Link
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  aria-current={isActive ? ariaCurrentFor(item.href, true) : undefined}
                  className={clsx(
                    "block w-full py-5 text-xl font-semibold uppercase transition-colors",
                    isActive ? "text-primary" : "text-white hover:text-primary",
                  )}
                >
                  {item.name}
                </Link>
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
}
